import { useState, useEffect } from 'react';
import { X, Copy, Eye, EyeOff } from 'lucide-react';
import { User } from '@/types/user';
import { Button, Input } from '@/components/ui';
import { generateSimplePassword } from '@/utils/passwordGenerator';
import { userService } from '@/services/userService';
import { useNotification } from '@/hooks/useNotification';

interface PasswordResetDialogProps {
  user: User | null;
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

export const PasswordResetDialog: React.FC<PasswordResetDialogProps> = ({
  user,
  isOpen,
  onClose,
  onSuccess
}) => {
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const { showError, showSuccess } = useNotification();

  useEffect(() => {
    // Auto-generate password when dialog opens
    if (isOpen && user && !password) {
      setTimeout(() => {
        const newPassword = generateSimplePassword();
        setPassword(newPassword);
      }, 100); // Small delay to ensure dialog is fully rendered
    }
    
    // Clear password when dialog closes
    if (!isOpen) {
      setPassword('');
      setShowPassword(false);
    }
  }, [isOpen, user]);

  const generatePassword = () => {
    const newPassword = generateSimplePassword();
    setPassword(newPassword);
  };

  const copyToClipboard = async () => {
    if (!password) return;
    
    try {
      await navigator.clipboard.writeText(password);
      showSuccess('Copied!', 'Password copied to clipboard');
    } catch (error) {
      showError('Copy Failed', 'Failed to copy password to clipboard');
    }
  };

  const handleSave = async () => {
    if (!user || !password.trim()) {
      showError('Invalid Data', 'Password is required');
      return;
    }

    setIsUpdating(true);
    try {
      const response = await userService.updateUser({
        db_id: user.db_id,
        fusion_auth_user_id: user.fusion_auth_user_id,
        password: password.trim()
      });

      const successMessage = response.message || `Password has been reset for ${user.first_name} ${user.last_name}`;
      
      showSuccess(
        'Password Updated',
        successMessage
      );
      
      // Add a small delay so user can see the success notification
      setTimeout(() => {
        onSuccess?.();
        handleClose();
      }, 1500); // 1.5 second delay
    } catch (error: any) {
      console.error('Password reset error:', error);
      
      if (error.isValidationError && error.validationData) {
        showError('Validation Error', error.validationData.message);
      } else {
        showError('Update Failed', error.message || 'Failed to reset password');
      }
    } finally {
      setIsUpdating(false);
    }
  };

  const handleClose = () => {
    setPassword('');
    setShowPassword(false);
    setIsUpdating(false);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999]">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4 relative z-[10000]">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900">
            Set New Password
          </h3>
          <button
            onClick={handleClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Body */}
        <div className="p-6 space-y-4" onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault(); // Prevent Enter from auto-submitting
          }
        }}>
          {user && (
            <div className="bg-gray-50 rounded-lg p-4">
              <div className="flex items-center space-x-3">
                <div className="h-10 w-10 bg-rgf-primary-100 rounded-full flex items-center justify-center">
                  <span className="text-sm font-medium text-rgf-primary-700">
                    {user.first_name?.[0]}{user.last_name?.[0]}
                  </span>
                </div>
                <div>
                  <div className="font-medium text-gray-900">
                    {user.first_name} {user.last_name}
                  </div>
                  <div className="text-sm text-gray-500">
                    {user.email}
                  </div>
                </div>
              </div>
            </div>
          )}

          <div className="space-y-3">
            <div className="flex justify-between items-center">
              <label className="block text-sm font-medium text-gray-700">
                New Password
              </label>
              <Button
                variant="secondary"
                size="sm"
                onClick={generatePassword}
                disabled={isUpdating}
              >
                Generate
              </Button>
            </div>
            
            <div className="relative">
              <Input
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter new password"
                disabled={isUpdating}
                className="pr-20"
              />
              <div className="absolute right-2 top-1/2 -translate-y-1/2 flex items-center space-x-1">
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="text-gray-400 hover:text-gray-600 p-1"
                  disabled={isUpdating}
                >
                  {showPassword ? (
                    <EyeOff className="h-4 w-4" />
                  ) : (
                    <Eye className="h-4 w-4" />
                  )}
                </button>
                {password && (
                  <button
                    type="button"
                    onClick={copyToClipboard}
                    className="text-gray-400 hover:text-gray-600 p-1"
                    disabled={isUpdating}
                    title="Copy to clipboard"
                  >
                    <Copy className="h-4 w-4" />
                  </button>
                )}
              </div>
            </div>

            {password && (
              <div className="text-sm text-gray-600 bg-blue-50 border border-blue-200 rounded-lg p-3">
                <p className="font-medium text-blue-800 mb-1">Important:</p>
                <p>Please copy this password and share it securely with the user. They should change it after first login.</p>
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end space-x-3 p-6 border-t border-gray-200">
          <Button
            type="button"
            variant="secondary"
            onClick={handleClose}
            disabled={isUpdating}
          >
            Cancel
          </Button>
          <Button
            type="button"
            onClick={handleSave}
            disabled={!password.trim() || isUpdating}
            loading={isUpdating}
          >
            {isUpdating ? 'Updating...' : 'Update Password'}
          </Button>
        </div>
      </div>
    </div>
  );
};